import { ChangeEvent, FormEvent, useCallback, useState } from 'react';
import { validateNumberInputValue } from '../utils';

type ValidatorFn = (value: string) => string | undefined;

export interface FieldOptions {
  required?: boolean | string;
  minLength?: { value: number; message: string };
  validate?: ValidatorFn;
  setValueAs?: (value: string) => string;
}

export type ValidationSchema<Values> = {
  [Field in keyof Values]: FieldOptions;
};

export const useForm = <Values extends Record<string, any>>(
  initialValues: Values,
  schema?: ValidationSchema<Values>
) => {
  const [values, setValues] = useState<Values>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof Values, string>>>(
    {}
  );
  const [isValid, setIsValid] = useState<boolean>(false);
  const [hasSubmittedOnce, setHasSubmittedOnce] = useState<boolean>(false);

  const validateField = useCallback(
    (name: keyof Values, value: string): string => {
      if (!schema) {
        return '';
      }

      const fieldOptions = schema[name];

      if (fieldOptions?.required && !value.trim()) {
        return typeof fieldOptions.required === 'string'
          ? fieldOptions.required
          : 'Поле обязательно для заполнения';
      }

      if (
        value.trim() &&
        fieldOptions?.minLength &&
        value.trim().length < fieldOptions?.minLength.value
      ) {
        return fieldOptions.minLength.message;
      }

      if (fieldOptions?.validate) {
        return fieldOptions.validate(value) || '';
      }

      return '';
    },
    [schema]
  );

  const validateAll = useCallback(() => {
    if (!schema) {
      return '';
    }

    let valid: boolean = true;
    const newErrors = {} as Partial<Record<keyof Values, string>>;

    (Object.keys(schema) as Array<keyof Values>).forEach((name) => {
      const error = validateField(name, String(values[name]));
      newErrors[name] = error;

      if (error) {
        valid = false;
      }
    });
    setErrors(newErrors);
    setIsValid(valid);

    return valid;
  }, [schema, validateField, values]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      const fieldName = name as keyof Values;
      let newValue = value;

      if (schema) {
        const fieldSchema = schema[fieldName];

        if (fieldSchema && fieldSchema.setValueAs) {
          newValue = fieldSchema.setValueAs(
            fieldName === 'phone' ? validateNumberInputValue(value) : value
          );
        }
      }

      setValues((prev) => ({
        ...prev,
        [fieldName]: newValue,
      }));

      if (hasSubmittedOnce) {
        const error = validateField(fieldName, newValue);
        setErrors((prev) => ({
          ...prev,
          [fieldName]: error,
        }));
      }
    },
    [schema, hasSubmittedOnce]
  );

  const handleSubmit = useCallback(
    (callback: (values: Values) => void) => {
      return (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (validateAll()) {
          callback(values);
        } else {
          setHasSubmittedOnce(true);
        }
      };
    },
    [validateAll, values]
  );

  const handleReset = useCallback(() => {
    setHasSubmittedOnce(false);
    setValues(initialValues);
    setErrors({});
    setIsValid(false);
  }, []);

  return { values, errors, handleChange, handleSubmit, handleReset, isValid };
};
